import { createAction } from '@reduxjs/toolkit';

import type {
  Outlets,
  Bundles,
  ItemNameEntity,
  SalesTypesEntity,
  FormItemEntity,
  ItemVariant,
  Image,
  User,
} from './types';

type SalesTypesForm = SalesTypesEntity & FormItemEntity;
type OutletsForm = Outlets & FormItemEntity;

function withPayloadType<T>() {
  return (t: T) => ({ payload: t });
}

export const pageRequest = createAction('libraryBundlePackage/pageRequest');

export const setOutlets = createAction<Outlets[]>(
  'libraryBundlePackage/setOutlets'
);

export const setBundlesList = createAction<Bundles[]>(
  'libraryBundlePackage/setBundlesList'
);

export const setIsCompleted = createAction<boolean>(
  'libraryBundlePackage/setIsCompleted'
);

export const setNextCursor = createAction<string>(
  'libraryBundlePackage/setNextCursor'
);

export const loadMoreBundles = createAction<string>(
  'libraryBundlePackage/loadMoreBundles'
);

export const resetBundlesListState = createAction(
  'libraryBundlePackage/resetBundlesListState'
);

export const updateListFilters = createAction<string[]>(
  'libraryBundlePackage/updateListFilters'
);

export const onClickRow = createAction<{ bundle: Bundles; outlets: Outlets[] }>(
  'libraryBundlePackage/onClickRow'
);

export const setBundleDetail = createAction(
  'libraryBundlePackage/setBundleDetail',
  withPayloadType<Bundles>()
);

export const unsetBundleDetail = createAction(
  'libraryBundlePackage/unsetBundleDetail'
);

export const refreshList = createAction<{
  outletId: string;
  isShowingDetail: boolean;
}>('libraryBundlePackage/refreshList');

export const hideBundleAssignOutletModal = createAction(
  'libraryBundlePackage/hideBundleAssignOutletModal'
);

export const showBundleAssignOutletModal = createAction(
  'libraryBundlePackage/showBundleAssignOutletModal'
);

export const onClickCreateBundle = createAction<Outlets[]>(
  'libraryBundlePackage/onClickCreateBundle'
);

export const searchOutlet = createAction<{ outlets: Outlets[]; query: string }>(
  'libraryBundlePackage/searchOutlet'
);

export const setSelectedOutlets = createAction<OutletsForm[]>(
  'libraryBundlePackage/setSelectedOutlets'
);

export const setInitialBundle = createAction(
  'libraryBundlePackage/setInitialBundle'
);

export const setAssignedBundleOutlets = createAction<OutletsForm[]>(
  'libraryBundlePackage/setAssignedBundleOutlets'
);

export const selectAllOutlets = createAction<{
  selectedOutlets: OutletsForm[];
  isCheckedAll: boolean;
}>('libraryBundlePackage/selectAllOutlets');

export const onClickCancelOutletAssign = createAction<OutletsForm[]>(
  'libraryBundlePackage/onClickCancelOutletAssign'
);

export const hideBundleManageSalesTypeModal = createAction(
  'libraryBundlePackage/hideBundleManageSalesTypeModal'
);

export const showBundleManageSalesTypeModal = createAction(
  'libraryBundlePackage/showBundleManageSalesTypeModal'
);

export const onClickAssignSalesType = createAction(
  'libraryBundlePackage/onClickAssignSalesType'
);

export const setSalesTypes = createAction<SalesTypesForm[]>(
  'libraryBundlePackage/setSalesTypes'
);

export const onSalesTypeSearchChange = createAction<{
  salesTypes: SalesTypesForm[];
  query: string;
}>('libraryBundlePackage/onSalesTypeSearchChange');

export const changeAllSalesType = createAction<{
  salesTypes: SalesTypesForm[];
  isCheckedAll: boolean;
}>('libraryBundlePackage/changeAllSalesType');

export const onSubmitAssignSalesType = createAction<SalesTypesForm[]>(
  'libraryBundlePackage/onSubmitAssignSalesType'
);

export const setAssignedBundleSalesTypes = createAction<SalesTypesForm[]>(
  'libraryBundlePackage/setAssignedBundleSalesTypes'
);

export const setIsEnabledBundleSalesTypes = createAction<{
  isEnabled: boolean;
  assignedOutlets: OutletsForm[];
  previousAssignedSalesTypes: SalesTypesForm[];
}>('libraryBundlePackage/setIsEnabledBundleSalesTypes');

export const disableSalesTypes = createAction(
  'libraryBundlePackage/disableSalesTypes'
);

export const onClickCancelManageSalesType = createAction<SalesTypesForm[]>(
  'libraryBundlePackage/onClickCancelManageSalesType'
);

export const setComponentsItemName = createAction<{
  itemName: ItemNameEntity;
  page: number;
}>('libraryBundlePackage/setComponentsItemName');

export const setComponentsItemVariantName = createAction<{
  [key: string]: ItemVariant;
}>('libraryBundlePackage/setComponentsItemVariantName');

export const setDeletedBundle = createAction(
  'libraryBundlePackage/setDeletedBundle',
  withPayloadType<Bundles>()
);

export const askDeleteBundle = createAction(
  'libraryBundlePackage/askDeleteBundle',
  withPayloadType<Bundles>()
);

export const cancelDeleteBundle = createAction(
  'libraryBundlePackage/cancelDeleteBundle'
);

export const showDeleteBundleModal = createAction(
  'libraryBundlePackage/showDeleteBundleModal'
);

export const hideDeleteBundleModal = createAction(
  'libraryBundlePackage/hideDeleteBundleModal'
);

export const onChangeOutletSalesType = createAction<{
  selectedOutlets: OutletsForm[];
  assignedSalesTypes: SalesTypesForm[];
}>('libraryBundlePackage/onChangeOutletSalesType');

export const showChangeOutletSalesTypeModal = createAction(
  'libraryBundlePackage/showChangeOutletSalesTypeModal'
);

export const hideChangeOutletSalesTypeModal = createAction(
  'libraryBundlePackage/hideChangeOutletSalesTypeModal'
);

export const confirmChangeOutletSalesType = createAction<OutletsForm[]>(
  'libraryBundlePackage/confirmChangeOutletSalesType'
);

export const deleteBundle = createAction<{
  bundle: Bundles;
  businessId: number;
}>('libraryBundlePackage/deleteBundle');

export const onSubmitCreateBundle = createAction<{
  bundle: Bundles;
  image: Image;
  user: User;
}>('libraryBundlePackage/onSubmitCreateBundle');

export const onSubmitUpdateBundle = createAction<{
  oldBundle: Bundles;
  bundle: Bundles;
  image: Image;
  user: User;
}>('libraryBundlePackage/onSubmitUpdateBundle');

export const setBundleOutletError = createAction<string>(
  'libraryBundlePackage/setBundleOutletError'
);

export const setBundleComponentsError = createAction<string>(
  'libraryBundlePackage/setBundleComponentsError'
);

export const resetBundleComponentsError = createAction(
  'libraryBundlePackage/resetBundleComponentsError'
);

export const setBundleSalesTypesError = createAction<string>(
  'libraryBundlePackage/setBundleSalesTypesError'
);

export const setUpdateBundleStatusConfirmation = createAction<{
  bundle: Bundles;
  isActivate: boolean;
}>('libraryBundlePackage/setUpdateBundleStatusConfirmation');

export const hideUpdateBundleStatusConfirmation = createAction<boolean>(
  'libraryBundlePackage/hideUpdateBundleStatusConfirmation'
);

export const updateBundleStatus = createAction<{
  bundle: Bundles;
  isActive: boolean;
  outletId: string;
}>('libraryBundlePackage/updateBundleStatus');

export const addNewItem = createAction<{
  searchKeyword: string;
  outletIds: number[];
  index: number;
}>('libraryBundlePackage/addNewItem');

export const closeBundleItemModal = createAction(
  'libraryBundlePackage/closeBundleItemModal'
);

export const onClickItemName = createAction<{
  outletIds: number[];
  itemName: string;
}>('libraryBundlePackage/onClickItemName');

export const loadMoreBundleItems = createAction<{
  previousBundleItems: any[];
  outletIds: number[];
  page: number;
  searchKeyword: string;
}>('libraryBundlePackage/loadMoreBundleItems');

export const onSubmitBundleItems = createAction<{
  itemName: string;
  itemVariantName: string;
  index: number;
}>('libraryBundlePackage/onSubmitBundleItems');

export const onRemoveBundleItems = createAction<number | string>(
  'libraryBundlePackage/onRemoveBundleItems'
);
